import { useState, useCallback } from 'react'
import { useSignTypedData, useAccount, useConnectorClient } from 'wagmi'
import type { Hex } from 'viem'

import { validationSigner, addressGenerationSigner, networkManagementSigner, tokenManagementSigner, adminManagementSigner, projectCreationSigner, projectUpdateSigner, referralSubmissionSigner, campaignImportSigner, sacrificeMutationSigner, reportToggleSigner, addressPoolUploadSigner, createSacrificeMutationMessage, createReportToggleMessage, loginString, reportTypes, instanceLoginSigner, instanceLoginString } from '../sacrifice-signers.js'
import type { ValidateMessage, LogType, NetworkManagementAction, NetworkManagementMessage, TokenManagementAction, TokenManagementMessage, AdminManagementAction, AdminManagementMessage, ProjectCreationMessage, ProjectUpdateMessage, ReferralSubmissionMessage, CampaignImportMessage, SacrificeMutationSigningInputs, ReportToggleSigningInputs, AddressPoolUploadMessage, InstanceLoginMessage } from '../sacrifice-signers.js'
import { NetworkType } from '../network-types.js'
import { createSacrificeDomain, defaultChainId } from '../constants.js'
import { EIP712Signer } from '../EIP712Signer.js'

export interface SignReportInputs {
  projectId: string,
  networkType: NetworkType,
  chainId: bigint,
  blockHash: string,
  tokenAddress: string,
  logIndex: number,
  logType: LogType,
  transactionHash: string,
  message: string,
  timestamp: bigint,
}

export interface SignNetworkManagementInputs {
  projectId: string
  networkType: string
  chainId: bigint
  action: NetworkManagementAction
  timestamp: bigint
  rootAddress?: string
  networkName?: string
  lowestBlock?: bigint
  lowerRange?: bigint
  upperRange?: bigint
  rpcUrls?: string
}

export interface SignTokenManagementInputs {
  projectId: string
  networkType: string
  chainId: bigint
  action: TokenManagementAction
  timestamp: bigint
  tokenAddress: string
  source?: string
  market?: string
  searchable?: string
}

export interface SignAdminManagementInputs {
  projectId: string
  address: string
  action: AdminManagementAction
  timestamp: bigint
}

export interface UseEIP712SignReturn {
  signValidate: (inputs: { projectId: string, termsOfUse: string, timestamp: bigint }) => Promise<Hex | null>
  signAddressGeneration: (inputs: { projectId: string, networkType: string, chainId: bigint, timestamp: bigint }) => Promise<Hex | null>
  signReport: (inputs: SignReportInputs) => Promise<Hex | null>
  signNetworkManagement: (inputs: SignNetworkManagementInputs) => Promise<Hex | null>
  signTokenManagement: (inputs: SignTokenManagementInputs) => Promise<Hex | null>
  signAdminManagement: (inputs: SignAdminManagementInputs) => Promise<Hex | null>
  signSacrificeMutation: (inputs: SacrificeMutationSigningInputs) => Promise<Hex | null>
  signReportToggle: (inputs: ReportToggleSigningInputs) => Promise<Hex | null>
  signProjectCreation: (inputs: ProjectCreationMessage) => Promise<Hex | null>
  signProjectUpdate: (inputs: ProjectUpdateMessage) => Promise<Hex | null>
  signCampaignImport: (message: CampaignImportMessage) => Promise<Hex | null>
  signReferralSubmission: (inputs: ReferralSubmissionMessage) => Promise<Hex | null>
  signAddressPoolUpload: (inputs: AddressPoolUploadMessage) => Promise<Hex | null>
  signInstanceLogin: (inputs: { timestamp: bigint }) => Promise<Hex | null>
  isSigning: boolean
  error: string | null
  resetError: () => void
}

export interface UseEIP712SignOptions { }

/**
 * React hook for EIP-712 signature generation
 * Provides convenient methods for signing different types of authentication messages
 */
export function useEIP712Sign(): UseEIP712SignReturn {
  const [error, setError] = useState<string | null>(null)
  const { isConnected } = useAccount()
  const { data: connectorClient } = useConnectorClient()

  const { signTypedDataAsync, isPending: isSigning } = useSignTypedData()

  const resetError = useCallback(() => {
    setError(null)
  }, [])

  const signMessage = useCallback(async (
    request: {
      domain: any;
      types: any;
      primaryType: string;
      message: any;
    }
  ): Promise<Hex | null> => {
    try {
      setError(null)

      // Check if wallet is connected
      if (!isConnected) {
        const errorMessage = 'Wallet not connected. Please connect your wallet first.'
        setError(errorMessage)
        console.error('EIP-712 signing failed:', errorMessage)
        return null
      }

      // Check if connector client is ready
      if (!connectorClient) {
        const errorMessage = 'Wallet connector not ready. Please try again in a moment.'
        setError(errorMessage)
        console.error('EIP-712 signing failed:', errorMessage)
        return null
      }

      const signature = await signTypedDataAsync({
        domain: request.domain,
        types: request.types,
        primaryType: request.primaryType,
        message: request.message,
      })

      return signature
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to sign message'
      setError(errorMessage)
      console.error('EIP-712 signing failed:', err)
      return null
    }
  }, [signTypedDataAsync, isConnected, connectorClient])

  const signValidate = useCallback(async ({ projectId, termsOfUse, timestamp }: {
    projectId: string,
    termsOfUse: string,
    timestamp: bigint,
  }): Promise<Hex | null> => {
    if (!projectId) {
      return null
    }
    // The login signature is bound to a specific campaign via projectId + that
    // campaign's disclaimer terms. Error handling lives in signMessage, the single
    // boundary; building the request cannot throw, so no wrapper is needed here.
    const message: ValidateMessage = {
      purpose: loginString,
      projectId,
      timestamp,
      termsOfUse,
    }
    return await signMessage(validationSigner.createSignatureRequest(message))
  }, [signMessage])

  const signInstanceLogin = useCallback(async ({ timestamp }: {
    timestamp: bigint,
  }): Promise<Hex | null> => {
    // Instance sign-in is project-independent: the message binds only the purpose
    // and a timestamp, so an admin proves wallet control without any campaign.
    const message: InstanceLoginMessage = {
      purpose: instanceLoginString,
      timestamp,
    }
    return await signMessage(instanceLoginSigner.createSignatureRequest(message))
  }, [signMessage])

  const signAddressGeneration = useCallback(async ({ projectId, networkType, chainId, timestamp }: {
    projectId: string,
    networkType: string,
    chainId: bigint,
    timestamp: bigint,
  }): Promise<Hex | null> => {
    if (!projectId) {
      return null
    }
    return await signMessage(addressGenerationSigner.createSignatureRequest({
      networkType,
      chainId,
      projectId,
      timestamp,
    }))
  }, [signMessage])

  const signReport = useCallback(async (inputs: SignReportInputs): Promise<Hex | null> => {
    // Use defaultChainId for the EIP-712 domain so users can report from any network;
    // the actual transaction chainId is included in the message data.
    const dynamicReportSigner = new EIP712Signer(
      createSacrificeDomain(defaultChainId),
      reportTypes,
      'Report'
    )
    return await signMessage(dynamicReportSigner.createSignatureRequest(inputs))
  }, [signMessage])

  const signNetworkManagement = useCallback(async (inputs: SignNetworkManagementInputs): Promise<Hex | null> => {
    if (!inputs.projectId) {
      return null
    }
    // Use empty strings/zeros for optional fields.
    const message: NetworkManagementMessage = {
      projectId: inputs.projectId,
      networkType: inputs.networkType as NetworkType,
      chainId: inputs.chainId,
      action: inputs.action,
      timestamp: inputs.timestamp,
      rootAddress: inputs.rootAddress || '',
      networkName: inputs.networkName || '',
      lowestBlock: inputs.lowestBlock || BigInt(0),
      lowerRange: inputs.lowerRange || BigInt(0),
      upperRange: inputs.upperRange || BigInt(0),
      rpcUrls: inputs.rpcUrls || '',
    }
    return await signMessage(networkManagementSigner.createSignatureRequest(message))
  }, [signMessage])

  const signTokenManagement = useCallback(async (inputs: SignTokenManagementInputs): Promise<Hex | null> => {
    if (!inputs.projectId) {
      return null
    }
    // Use empty strings for optional fields.
    const message: TokenManagementMessage = {
      projectId: inputs.projectId,
      networkType: inputs.networkType as NetworkType,
      chainId: inputs.chainId,
      action: inputs.action,
      timestamp: inputs.timestamp,
      tokenAddress: inputs.tokenAddress,
      source: inputs.source || '',
      market: inputs.market || '',
      searchable: inputs.searchable || '',
    }
    return await signMessage(tokenManagementSigner.createSignatureRequest(message))
  }, [signMessage])

  const signAdminManagement = useCallback(async (inputs: SignAdminManagementInputs): Promise<Hex | null> => {
    if (!inputs.projectId) {
      return null
    }
    const message: AdminManagementMessage = {
      projectId: inputs.projectId,
      address: inputs.address,
      action: inputs.action,
      timestamp: inputs.timestamp,
    }
    return await signMessage(adminManagementSigner.createSignatureRequest(message))
  }, [signMessage])

  const signSacrificeMutation = useCallback(async (inputs: SacrificeMutationSigningInputs): Promise<Hex | null> => {
    if (!inputs.projectId) {
      return null
    }
    // The shared message builder applies the optional-field sentinel encoding,
    // so the backend reconstructs the identical message from validated inputs.
    return await signMessage(sacrificeMutationSigner.createSignatureRequest(createSacrificeMutationMessage(inputs)))
  }, [signMessage])

  const signReportToggle = useCallback(async (inputs: ReportToggleSigningInputs): Promise<Hex | null> => {
    if (!inputs.projectId) {
      return null
    }
    return await signMessage(reportToggleSigner.createSignatureRequest(createReportToggleMessage(inputs)))
  }, [signMessage])

  const signProjectCreation = useCallback(async (inputs: ProjectCreationMessage): Promise<Hex | null> => {
    if (!inputs.projectId) {
      return null
    }
    return await signMessage(projectCreationSigner.createSignatureRequest(inputs))
  }, [signMessage])

  const signProjectUpdate = useCallback(async (inputs: ProjectUpdateMessage): Promise<Hex | null> => {
    if (!inputs.projectId) {
      return null
    }
    return await signMessage(projectUpdateSigner.createSignatureRequest(inputs))
  }, [signMessage])

  const signCampaignImport = useCallback(async (message: CampaignImportMessage): Promise<Hex | null> => {
    if (!message.config) {
      return null
    }
    return await signMessage(campaignImportSigner.createSignatureRequest(message))
  }, [signMessage])

  const signReferralSubmission = useCallback(async (inputs: ReferralSubmissionMessage): Promise<Hex | null> => {
    if (!inputs.projectId) {
      return null
    }
    return await signMessage(referralSubmissionSigner.createSignatureRequest(inputs))
  }, [signMessage])

  const signAddressPoolUpload = useCallback(async (inputs: AddressPoolUploadMessage): Promise<Hex | null> => {
    if (!inputs.projectId) {
      return null
    }
    return await signMessage(addressPoolUploadSigner.createSignatureRequest(inputs))
  }, [signMessage])

  return {
    signValidate,
    signAddressGeneration,
    signReport,
    signNetworkManagement,
    signTokenManagement,
    signAdminManagement,
    signSacrificeMutation,
    signReportToggle,
    signProjectCreation,
    signProjectUpdate,
    signCampaignImport,
    signReferralSubmission,
    signAddressPoolUpload,
    signInstanceLogin,
    isSigning,
    error,
    resetError,
  }
}
